import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fireEvent } from "@/lib/events";
import { startOfIsoWeek } from "@/lib/isoWeek";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "./useAuth";
import type { Subscription, SubscriptionTier, TierSlug } from "@/lib/supabase";

export type TierId = TierSlug;

export interface CapStatus {
  /** true once `current >= max`. Always false when `max` is null (unlimited). */
  atCap: boolean;
  current: number;
  /** null = unlimited */
  max: number | null;
}

export interface Entitlements {
  // ── Identity ────────────────────────────────────────────────────────────
  tier: TierId;
  tierData: SubscriptionTier | null;
  subscription: Subscription | null;
  isLoading: boolean;
  purchaseSource: "web" | "ios" | "android";

  // ── Feature gates (can the user take this action right now?) ────────────
  // canAI is vestigial since Mori-for-all — every tier has `ai_processing`
  // true now. Gate new AI entry points on `needsAdForAI` instead.
  canAI: boolean;
  canVoiceMemory: boolean;
  canVideoMemory: boolean;
  canDrawing: boolean;
  canCollections: boolean;
  canCustomCollections: boolean;
  canActiveCapture: boolean;
  canVoiceContext: boolean;
  canMapPin: boolean;
  canChildCreator: boolean;
  needsAdForAI: boolean;
  showAds: boolean;

  // ── Duration & people caps (raw values from the tier row) ───────────────
  // null = unlimited on every *Max field.
  maxVoiceDurationSeconds: number | null;
  maxVideoDurationSeconds: number | null;
  maxPhotosPerMemory: number;
  maxMembers: number | null;
  maxContributors: number | null;

  // ── Usage counters (calendar-window) ────────────────────────────────────
  voiceMemoriesThisWeek: number;
  voiceRemainingThisWeek: number | null;
  videoMemoriesThisWeek: number;
  videoRemainingThisWeek: number | null;
  drawingsThisMonth: number;
  drawingsRemaining: number | null;
  capsulesThisYear: number;
  capsulesRemaining: number | null;

  // ── Soft-cap helpers ────────────────────────────────────────────────────
  membersAtCap: CapStatus;
  contributorsAtCap: CapStatus;

  // ── Storage ─────────────────────────────────────────────────────────────
  storageUsedMb: number;
  storageLimitMb: number;
  bonusStorageMb: number;
  storageUsedPercent: number;
  isStorageFull: boolean;
  isNearStorage: boolean;

  // ── Retention & sharing ─────────────────────────────────────────────────
  audioRetentionDays: number | null;
  shareLinkHours: number | null;

  // ── Derived ─────────────────────────────────────────────────────────────
  canRecord: boolean;
  canCreateCapsule: boolean;
  isAtDrawingLimit: boolean;

  // ── Lifecycle ───────────────────────────────────────────────────────────
  scheduledTier: string | null;
  scheduledAt: Date | null;
  periodEnd: Date | null;
  lastChangedAt: Date | null;

  // ── Mori style ──────────────────────────────────────────────────────────
  moriStyle: { stage: string; personality: string; custom_overrides: Record<string, unknown> };

  refresh: () => void;
}

interface UsageRow {
  memory_type: string | null;
  capsule_state: string | null;
  created_at: string;
}

interface MemberRow {
  role: string;
}

const EMPTY_CAP: CapStatus = { atCap: false, current: 0, max: null };

const EMPTY: Entitlements = {
  tier: "seedling",
  tierData: null,
  subscription: null,
  isLoading: true,
  purchaseSource: "web",
  canAI: false,
  canVoiceMemory: false,
  canVideoMemory: false,
  canDrawing: false,
  canCollections: false,
  canCustomCollections: false,
  canActiveCapture: false,
  canVoiceContext: false,
  canMapPin: false,
  canChildCreator: false,
  needsAdForAI: false,
  showAds: false,
  maxVoiceDurationSeconds: null,
  maxVideoDurationSeconds: null,
  maxPhotosPerMemory: 1,
  maxMembers: null,
  maxContributors: null,
  voiceMemoriesThisWeek: 0,
  voiceRemainingThisWeek: null,
  videoMemoriesThisWeek: 0,
  videoRemainingThisWeek: null,
  drawingsThisMonth: 0,
  drawingsRemaining: null,
  capsulesThisYear: 0,
  capsulesRemaining: null,
  membersAtCap: EMPTY_CAP,
  contributorsAtCap: EMPTY_CAP,
  storageUsedMb: 0,
  storageLimitMb: 0,
  bonusStorageMb: 0,
  storageUsedPercent: 0,
  isStorageFull: false,
  audioRetentionDays: null,
  shareLinkHours: null,
  canRecord: true,
  canCreateCapsule: false,
  isAtDrawingLimit: false,
  isNearStorage: false,
  scheduledTier: null,
  scheduledAt: null,
  periodEnd: null,
  lastChangedAt: null,
  moriStyle: { stage: "explorer", personality: "default", custom_overrides: {} },
  refresh: () => {},
};

/**
 * Tier entitlement snapshot as stored on `tier_history.snapshot` — effectively
 * a `SubscriptionTier` row frozen at a point in time. Kept loose (optional
 * fields) so historical snapshots written before the locked matrix columns
 * existed still type-check.
 */
export type TierSnapshot = Partial<SubscriptionTier> & {
  id: TierSlug | string;
  name?: string;
};

/**
 * Resolve the entitlement snapshot a specific memory was created under.
 * Follows memories.created_under_tier + created_at into tier_history,
 * selecting the row whose [effective_from, effective_to) bracket contains
 * the memory's created_at. Returns null if no matching snapshot exists.
 */
export async function getTierForMemory(memory: {
  created_under_tier: string;
  created_at: string;
}): Promise<TierSnapshot | null> {
  const { data } = await supabase
    .from("tier_history")
    .select("snapshot")
    .eq("tier_slug", memory.created_under_tier)
    .lte("effective_from", memory.created_at)
    .or(`effective_to.is.null,effective_to.gt.${memory.created_at}`)
    .order("effective_from", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!data) return null;
  return (data as { snapshot: TierSnapshot }).snapshot ?? null;
}

/** Invalidate all entitlements queries. Call after any mutation that changes usage or subscription. */
export function invalidateEntitlements() {
  return queryClient.invalidateQueries({ queryKey: ['entitlements'] });
}

async function fetchEntitlements(userId: string): Promise<Omit<Entitlements, 'refresh'>> {
  const [
    { data: sub },
    { data: usageRows },
    { data: profileRow },
    { data: memberRows },
  ] = await Promise.all([
    supabase
      .from("subscriptions")
      .select("*, tier:subscription_tiers!subscriptions_tier_id_fkey(*)")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("memories")
      .select("memory_type, capsule_state, created_at")
      .eq("user_id", userId)
      .not("space_id", "is", null),
    supabase
      .from("profiles")
      .select("bonus_storage_mb, storage_used_mb, mori_style")
      .eq("id", userId)
      .maybeSingle(),
    supabase
      .from("space_members")
      .select("role, spaces!inner(owner_id, deleted_at)")
      .eq("spaces.owner_id", userId)
      .is("spaces.deleted_at", null)
      .neq("role", "owner")
      .in("status", ["active", "pending"]),
  ]);

  const profile = profileRow as {
    bonus_storage_mb: number | null;
    storage_used_mb: number | null;
    mori_style: { stage: string; personality: string; custom_overrides: Record<string, unknown> } | null;
  } | null;
  const bonusStorageMb = Number(profile?.bonus_storage_mb ?? 0);
  const storageUsedMb = Number(profile?.storage_used_mb ?? 0);
  const moriStyle = profile?.mori_style
    ?? { stage: "explorer", personality: "default", custom_overrides: {} };

  const subscription = (sub as (Subscription & { tier: SubscriptionTier }) | null);
  const tierData = subscription?.tier ?? null;
  const tier = (subscription?.tier_id as TierId | undefined) ?? "seedling";
  const purchaseSource =
    (subscription?.purchase_source as "web" | "ios" | "android" | undefined) ?? "web";

  const usage: UsageRow[] = (usageRows as UsageRow[] | null) ?? [];

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const weekStart = startOfIsoWeek(now);

  let drawingsThisMonth = 0;
  let capsulesThisYear = 0;
  let voiceMemoriesThisWeek = 0;
  let videoMemoriesThisWeek = 0;

  for (const row of usage) {
    const created = new Date(row.created_at);
    if (row.memory_type === "drawing" && created >= monthStart) drawingsThisMonth++;
    if (row.capsule_state === "time_capsule" && created >= yearStart) capsulesThisYear++;
    if (row.memory_type === "voice_moment" && created >= weekStart) voiceMemoriesThisWeek++;
    if (row.memory_type === "shortie" && created >= weekStart) videoMemoriesThisWeek++;
  }

  const storageLimitMb = (tierData?.storage_mb ?? 0) + bonusStorageMb;
  const storageUsedPercent =
    storageLimitMb > 0 ? Math.ceil((storageUsedMb / storageLimitMb) * 100) : 0;
  const isStorageFull = storageUsedMb >= storageLimitMb && storageLimitMb > 0;
  const isNearStorage = storageUsedPercent >= 80;

  const maxVoiceDurationSeconds = tierData?.max_voice_duration_seconds ?? null;
  const maxVideoDurationSeconds = tierData?.max_video_duration_seconds ?? null;
  const maxPhotosPerMemory = tierData?.max_photos_per_memory ?? 1;
  const maxMembers = tierData?.max_members ?? null;
  const maxContributors = tierData?.max_contributors ?? null;

  const maxVoicePerWeek = tierData?.max_voice_per_week ?? null;
  const maxVideoPerWeek = tierData?.max_video_per_week ?? null;
  const maxDrawings = tierData?.max_drawings_per_month ?? null;
  const maxCapsules = tierData?.max_time_capsules_per_year ?? null;

  const voiceRemainingThisWeek =
    maxVoicePerWeek === null ? null : Math.max(0, maxVoicePerWeek - voiceMemoriesThisWeek);
  const videoRemainingThisWeek =
    maxVideoPerWeek === null ? null : Math.max(0, maxVideoPerWeek - videoMemoriesThisWeek);
  const drawingsRemaining =
    maxDrawings === null ? null : Math.max(0, maxDrawings - drawingsThisMonth);
  const capsulesRemaining =
    maxCapsules === null ? null : Math.max(0, maxCapsules - capsulesThisYear);

  const members = (memberRows as MemberRow[] | null) ?? [];
  let memberCount = 0;
  let contributorCount = 0;
  for (const row of members) {
    if (row.role === "member") memberCount++;
    else if (row.role === "contributor") contributorCount++;
  }
  const membersAtCap: CapStatus = {
    current: memberCount,
    max: maxMembers,
    atCap: maxMembers !== null && memberCount >= maxMembers,
  };
  const contributorsAtCap: CapStatus = {
    current: contributorCount,
    max: maxContributors,
    atCap: maxContributors !== null && contributorCount >= maxContributors,
  };

  const canAI = tierData?.ai_processing ?? false;
  const canDrawing = tierData?.drawings_enabled ?? false;
  const canCollections = tierData?.collections_enabled ?? false;
  const canCustomCollections = tierData?.can_custom_collections ?? false;
  const canActiveCapture = tierData?.can_active_capture ?? false;
  const canVoiceContext = tierData?.can_voice_context ?? false;
  const canMapPin = tierData?.can_map_pin ?? false;
  const canChildCreator = tierData?.can_child_creator ?? false;
  const needsAdForAI = tierData?.needs_ad_for_ai ?? false;
  const showAds = tierData?.ads_enabled ?? false;

  const canVoiceMemory = voiceRemainingThisWeek === null || voiceRemainingThisWeek > 0;
  const canVideoMemory = videoRemainingThisWeek === null || videoRemainingThisWeek > 0;
  const isAtDrawingLimit = maxDrawings !== null && drawingsThisMonth >= maxDrawings;
  const canCreateCapsule = maxCapsules === null || capsulesThisYear < maxCapsules;

  const cleanSub = subscription
    ? ({ ...subscription, tier: undefined } as unknown as Subscription)
    : null;

  return {
    tier,
    tierData,
    subscription: cleanSub,
    isLoading: false,
    purchaseSource,
    canAI,
    canVoiceMemory,
    canVideoMemory,
    canDrawing,
    canCollections,
    canCustomCollections,
    canActiveCapture,
    canVoiceContext,
    canMapPin,
    canChildCreator,
    needsAdForAI,
    showAds,
    maxVoiceDurationSeconds,
    maxVideoDurationSeconds,
    maxPhotosPerMemory,
    maxMembers,
    maxContributors,
    voiceMemoriesThisWeek,
    voiceRemainingThisWeek,
    videoMemoriesThisWeek,
    videoRemainingThisWeek,
    drawingsThisMonth,
    drawingsRemaining,
    capsulesThisYear,
    capsulesRemaining,
    membersAtCap,
    contributorsAtCap,
    storageUsedMb,
    storageLimitMb,
    bonusStorageMb,
    storageUsedPercent,
    isStorageFull,
    isNearStorage,
    audioRetentionDays: tierData?.audio_retention_days ?? null,
    shareLinkHours: tierData?.share_link_hours ?? null,
    canRecord: !isStorageFull,
    canCreateCapsule,
    isAtDrawingLimit,
    scheduledTier: cleanSub?.scheduled_tier_id ?? null,
    scheduledAt: cleanSub?.scheduled_at ? new Date(cleanSub.scheduled_at) : null,
    periodEnd: cleanSub?.current_period_end ? new Date(cleanSub.current_period_end) : null,
    lastChangedAt: cleanSub?.last_changed_at ? new Date(cleanSub.last_changed_at) : null,
    moriStyle,
  };
}

/**
 * Real entitlements + usage tracking. Pulls subscription + tier in one query
 * and computes per-week / per-month / per-year usage from the memories table.
 */
export function useEntitlements(): Entitlements {
  const { user } = useAuth();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase.rpc('claim_subscription_upgrade');
      const row = (data as Array<{ from_tier: string; to_tier: string }> | null)?.[0];
      if (cancelled || !row) return;
      fireEvent('subscription.upgraded', {
        userId,
        fromTier: row.from_tier,
        toTier: row.to_tier,
      });
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const query = useQuery({
    queryKey: ['entitlements', userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: () => fetchEntitlements(userId!),
  });

  const base = query.data ?? EMPTY;
  return {
    ...base,
    isLoading: query.isLoading,
    refresh: () => { void query.refetch(); },
  };
}
